import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Filter, ChevronDown } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { ProductCard } from '@/components/shop/ProductCard';
import { useProducts } from '@/hooks/useProducts';
import { Loader2 } from 'lucide-react';
import { PageSeoHead } from '@/components/seo/PageSeoHead';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const Shop = () => {
  const { t, i18n } = useTranslation();
  const [selectedCollection, setSelectedCollection] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  const collections = useMemo(() => [
    { value: 'all', label: t('shop.allCollections') },
    { value: 'tag:Montagna', label: t('shop.collections.mountain') },
    { value: 'tag:Fauna', label: t('shop.collections.fauna') },
    { value: 'tag:equitazione', label: t('shop.collections.equestrian') },
  ], [t]);

  const productTypes = useMemo(() => [
    { value: 'all', label: t('shop.allTypes') },
    { value: 'Braccialetto', label: t('shop.types.bracelet') },
    { value: 'Collana', label: t('shop.types.necklace') },
    { value: 'Cintura', label: t('shop.types.belt') },
    { value: 'Orecchini', label: t('shop.types.earrings') },
    { value: 'Portachiavi', label: t('shop.types.keychain') },
    { value: 'Tappo', label: t('shop.types.cork') },
    { value: 'Spilla', label: t('shop.types.pin') },
  ], [t]);

  // Build query string based on filters
  const query = useMemo(() => {
    const parts: string[] = [];
    if (selectedCollection !== 'all') parts.push(selectedCollection);
    if (selectedType !== 'all') parts.push(`product_type:${selectedType}`);
    return parts.length > 0 ? parts.join(' AND ') : undefined;
  }, [selectedCollection, selectedType]);

  const { data: products, isLoading, error } = useProducts(250, query);

  const selectedCollectionLabel = collections.find(c => c.value === selectedCollection)?.label || t('shop.allCollections');
  const selectedTypeLabel = productTypes.find(pt => pt.value === selectedType)?.label || t('shop.allTypes');

  const isIT = i18n.language === 'it';

  return (
    <Layout>
      <PageSeoHead
        title={isIT
          ? 'Negozio Gioielli Artigianali in Bronzo | Sampaoli Creazioni'
          : 'Shop Handcrafted Bronze Jewelry | Sampaoli'}
        description={isIT
          ? 'Acquista gioielli artigianali in bronzo fatti a mano in Italia. Braccialetti, collane, cinture e spille ispirati a montagna, fauna e mondo equestre.'
          : 'Shop handcrafted bronze jewelry made in Italy. Bracelets, necklaces, belts and pins inspired by mountains, wildlife and equestrian world.'}
        path="/shop"
      />
      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-secondary">
        <div className="container-wide text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-body text-xs md:text-sm uppercase tracking-[0.3em] text-bronze mb-3 block">
              {t('shop.heroLabel')}
            </span>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
              {t('shop.heroTitle')}
            </h1>
            <p className="font-elegant text-lg text-muted-foreground italic max-w-2xl mx-auto">
              {t('shop.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-6 bg-background border-b border-bronze/10">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">{t('shop.filters')}:</span>
            </div>

            {/* Collection Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 border-bronze/20 hover:border-bronze/40 hover:bg-secondary/50">
                  {selectedCollectionLabel}
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-background border-bronze/20">
                {collections.map((collection) => (
                  <DropdownMenuItem
                    key={collection.value}
                    onClick={() => setSelectedCollection(collection.value)}
                    className={selectedCollection === collection.value ? 'bg-secondary' : ''}
                  >
                    {collection.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Type Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 border-bronze/20 hover:border-bronze/40 hover:bg-secondary/50">
                  {selectedTypeLabel}
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-background border-bronze/20">
                {productTypes.map((type) => (
                  <DropdownMenuItem
                    key={type.value}
                    onClick={() => setSelectedType(type.value)}
                    className={selectedType === type.value ? 'bg-secondary' : ''}
                  >
                    {type.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container mx-auto px-4 max-w-7xl">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-bronze" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">{t('shop.error')}</p>
            </div>
          ) : products && products.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground mb-6">
                {t('shop.showing', { count: products.length })}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {products.map((product, index) => (
                  <ProductCard key={product.node.id} product={product} index={index} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground">{t('shop.noProducts')}</p>
              {(selectedCollection !== 'all' || selectedType !== 'all') && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setSelectedCollection('all');
                    setSelectedType('all');
                  }}
                >
                  {t('shop.removeFilters')}
                </Button>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Shop;
